import type { ReactNode } from 'react';
import { Breadcrumbs, MarkWaiting, type Crumb } from '../../components/ui/index.js';
import { usePublishCrumbs } from '../../components/ui/shell-slot.js';

/**
 * The record bar of a sample or a receipt page (D278, the approved
 * redesign's `recbar`).
 *
 * TOP ROW: the record number — a machine identifier, so left-to-right and in
 * the measure face in both languages — beside its status badges; the title
 * under it; the key context (client, project) as one quiet line; and the
 * page's actions at the trailing end, the next step last and alone in the
 * primary style.
 *
 * "WAITING ON" is said in words, in the waiting tone, when the record is
 * waiting on another person's act. It only describes the state the server
 * reported; it never decides anything.
 *
 * Breadcrumbs go to the shell's top bar exactly as `PageHeader` sends them;
 * outside the shell (a component test) they are drawn here instead.
 */
export function RecordHeader({
  number,
  badges,
  title,
  context,
  actions,
  waitingOn,
  breadcrumbs,
}: {
  readonly number: string;
  readonly badges?: ReactNode;
  /** Already translated. */
  readonly title: string;
  readonly context?: readonly { readonly key: string; readonly label: string; readonly value: ReactNode }[];
  readonly actions?: ReactNode;
  /** Already translated; omitted when nobody else is awaited. */
  readonly waitingOn?: string | null;
  readonly breadcrumbs?: readonly Crumb[];
}) {
  const inShell = usePublishCrumbs(breadcrumbs);
  return (
    <header className="mb-gutter rounded-large border border-default-200 bg-white px-4 py-3">
      {breadcrumbs && !inShell ? (
        <div className="mb-2">
          <Breadcrumbs items={breadcrumbs} />
        </div>
      ) : null}
      <div className="flex flex-wrap items-start gap-x-stack gap-y-3">
        <div className="min-w-0 grow basis-56">
          <div className="flex flex-wrap items-center gap-inline">
            <span className="measure text-section font-semibold text-default-900" dir="ltr">
              {number}
            </span>
            {badges}
          </div>
          <h1 className="mt-0.5 text-title font-bold text-default-900">{title}</h1>
          {context && context.length > 0 ? (
            <dl className="mt-1 flex flex-wrap gap-x-gutter gap-y-1 text-body">
              {context.map((item) => (
                <div key={item.key} className="flex min-w-0 items-baseline gap-1.5">
                  <dt className="text-caption text-default-600">{item.label}</dt>
                  <dd className="min-w-0 break-words text-default-900">{item.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center justify-end gap-inline">{actions}</div>
        ) : null}
      </div>
      {waitingOn ? (
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-medium bg-waiting-50 px-2 py-1 text-caption font-medium text-waiting-700">
          <MarkWaiting size={14} />
          {waitingOn}
        </p>
      ) : null}
    </header>
  );
}

/**
 * A link to a related record, in the record bar or a chain of records. A
 * record number keeps its own direction; a name is entered data and keeps
 * whatever direction it was typed in.
 */
export const recordLinkClass =
  'font-medium text-primary-700 underline-offset-2 hover:text-primary-800 hover:underline';
